import React, { useState, useMemo } from 'react'
import classNames from 'classnames'

/**
 * Minimal replacement for react-bootstrap-table-next (react-16-only, unmaintained)
 * keeping its column-def shape so the webapp analytics tables port verbatim:
 *   columns: [{ dataField, text, formatter(cell, row), headerStyle, style,
 *               align, sort, headerTitle: () => string }]
 *   expandRow: { renderer(row), className, parentClassName } — row click toggles
 *   defaultSorted: [{ dataField, order: 'asc'|'desc' }]
 *   hideHeader — replaces per-column headerAttrs.hidden used by member tables
 */
const ReportTable = ({
  keyField,
  data,
  columns,
  wrapperClasses = '',
  rowClasses = '',
  noDataIndication,
  expandRow,
  defaultSorted,
  hideHeader = false
}) => {
  const [sortState, setSortState] = useState(defaultSorted?.[0] || null)
  const [expandedRows, setExpandedRows] = useState(() => new Set())

  const sortedData = useMemo(() => {
    if (!sortState) return data
    const { dataField, order } = sortState
    const sorted = [...data].sort((a, b) => {
      const aValue = a[dataField]
      const bValue = b[dataField]
      let compared
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        compared = aValue - bValue
      } else {
        compared = String(aValue ?? '').localeCompare(String(bValue ?? ''))
      }
      return order === 'desc' ? -compared : compared
    })
    return sorted
  }, [data, sortState])

  const onHeaderClick = (column) => {
    if (!column.sort) return
    setSortState((prev) => {
      if (prev && prev.dataField === column.dataField) {
        return { dataField: column.dataField, order: prev.order === 'asc' ? 'desc' : 'asc' }
      }
      return { dataField: column.dataField, order: 'asc' }
    })
  }

  const getSortIcon = (column) => {
    if (!column.sort) return null
    if (sortState?.dataField === column.dataField) {
      return <i className={`fa ${sortState.order === 'asc' ? 'fa-sort-asc' : 'fa-sort-desc'} ms-1`} />
    }
    return <i className="fa fa-sort ms-1 text-muted" />
  }

  const toggleRowExpansion = (rowKey) => {
    if (!expandRow) return
    setExpandedRows((prev) => {
      const next = new Set(prev)
      if (next.has(rowKey)) {
        next.delete(rowKey)
      } else {
        next.add(rowKey)
      }
      return next
    })
  }

  return (
    <div className={wrapperClasses}>
      <table className="table">
        {!hideHeader && (
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.dataField}
                  style={{
                    ...column.headerStyle,
                    ...(column.sort ? { cursor: 'pointer' } : {}),
                    ...(column.align ? { textAlign: column.align } : {})
                  }}
                  title={column.headerTitle ? column.headerTitle(column) : undefined}
                  onClick={() => onHeaderClick(column)}
                >
                  {column.headerFormatter
                    ? column.headerFormatter(column, 0, { sortElement: getSortIcon(column) })
                    : (
                      <>
                        {column.text}
                        {getSortIcon(column)}
                      </>
                    )}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {sortedData.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                style={{ padding: 0 }}
              >
                {noDataIndication ? noDataIndication() : null}
              </td>
            </tr>
          ) : (
            sortedData.map((row) => {
              const rowKey = row[keyField]
              const isExpanded = expandRow ? expandedRows.has(rowKey) : false
              return (
                <React.Fragment key={rowKey}>
                  <tr
                    className={classNames(rowClasses, { [expandRow?.parentClassName || '']: isExpanded })}
                    style={expandRow ? { cursor: 'pointer' } : undefined}
                    onClick={() => toggleRowExpansion(rowKey)}
                  >
                    {columns.map((column) => (
                      <td
                        key={column.dataField}
                        style={{
                          ...column.style,
                          ...(column.align ? { textAlign: column.align } : {})
                        }}
                      >
                        {column.formatter
                          ? column.formatter(row[column.dataField], row)
                          : row[column.dataField]}
                      </td>
                    ))}
                  </tr>
                  {isExpanded && (
                    <tr className={expandRow.className}>
                      <td colSpan={columns.length}>
                        {expandRow.renderer(row)}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              )
            })
          )}
        </tbody>
      </table>
    </div>
  )
}

export default ReportTable
